<script setup>
import { computed, ref, watch } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import KaptionNoteCard from './KaptionNoteCard.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
// NOTICE: KAPTION-INTEGRATION
import KaptionTranslationDisplay from 'dashboard/components-next/message/kaption/KaptionTranslationDisplay.vue';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useUserLanguage } from 'dashboard/composables/useUserLanguage';
import { parseKaptionNoteCard } from 'dashboard/helper/kaptionNoteCards';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import KaptionPhoneRequestIcon from 'dashboard/components/kaption/KaptionPhoneRequestIcon.vue';

// NOTICE: KAPTION-INTEGRATION - Add id, isMessageTranslating, and isBotPrivateNote to message context
const {
  content,
  attachments,
  contentAttributes,
  messageType,
  conversationId,
  id,
  private: isPrivate,
  isMessageTranslating,
  isBotPrivateNote,
} = useMessageContext();

const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
const { canUseFeature } = useFeaturePermissions();

// NOTICE: KAPTION-INTEGRATION - Watch contentAttributes to ensure reactivity from ActionCable updates
watch(
  () => contentAttributes.value,
  () => {
    // Content attributes changed
  },
  { deep: true }
);

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const { userLanguage } = useUserLanguage();

// NOTICE: KAPTION-INTEGRATION
// Get the message's source language from translation metadata
const messageSourceLanguage = computed(() => {
  const attrs = contentAttributes.value;
  if (!attrs) return null;

  // NOTICE: Check old format FIRST - it often has more reliable source_language
  // (especially for older messages that were translated before the new format)
  const oldTranslation = attrs.kaptionTranslation;
  if (oldTranslation) {
    // NOTICE: KAPTION-INTEGRATION - Check camelCase first after deep conversion
    const sourceLang =
      oldTranslation.sourceLanguage || oldTranslation.source_language;
    if (sourceLang && sourceLang !== 'unknown' && sourceLang !== 'Unknown') {
      return sourceLang;
    }
  }

  // Check new format (multiple translations)
  const translations = attrs.kaptionTranslations;
  if (translations && typeof translations === 'object') {
    // Get source_language from ANY translation
    // NOTICE: Prioritize translations with valid source_language (not "unknown")
    // Some old translations might have "unknown" which is not useful
    let validSourceLang = null;
    let fallbackSourceLang = null;

    const translationKeys = Object.keys(translations);
    for (let i = 0; i < translationKeys.length; i += 1) {
      const lang = translationKeys[i];
      const translation = translations[lang];
      // NOTICE: KAPTION-INTEGRATION - Check camelCase first after deep conversion
      const sourceLang =
        translation?.sourceLanguage || translation?.source_language;

      if (sourceLang) {
        // Prefer valid language codes over "unknown"
        if (sourceLang !== 'unknown' && sourceLang !== 'Unknown') {
          validSourceLang = sourceLang;
          break;
        } else {
          // Keep "unknown" as fallback if nothing better found
          fallbackSourceLang = sourceLang;
        }
      }
    }

    // Return valid source language if found, otherwise fallback
    if (validSourceLang) {
      return validSourceLang;
    }
    if (fallbackSourceLang) {
      return fallbackSourceLang;
    }
  }

  return null;
});

// NOTICE: KAPTION-INTEGRATION
// Check if Kaption live translation is available (using camelCase for frontend)
// Supports both new multi-language format and old single translation format
// BUT: Don't show if message is already in user's language
const hasKaptionTranslation = computed(() => {
  const attrs = contentAttributes.value;
  if (!attrs) return false;

  const userLang = userLanguage?.value;
  const sourceLang = messageSourceLanguage.value;

  // If message is already in user's language, don't show translation UI
  if (sourceLang && userLang && sourceLang === userLang) {
    return false;
  }

  // Check new format first (multiple translations)
  const translations = attrs.kaptionTranslations;
  if (translations && typeof translations === 'object') {
    // NOTICE: KAPTION-INTEGRATION
    // Check if translation exists for the ACTUAL user's language
    // NOT just if ANY translation exists
    // If no translation for user's language, we should not render KaptionTranslationDisplay
    // because KaptionTranslationDisplay will return null and hide the original message

    // First check if source_language matches user_language (no translation needed)
    if (sourceLang && userLang && sourceLang === userLang) {
      return false;
    }

    // NOTICE: KAPTION-INTEGRATION - Check if translation TEXT exists, then return boolean
    const hasUserLanguageTranslation = !!(
      userLang && translations[userLang]?.text
    );

    // Also check for account locale fallback
    let hasAccountLocaleTranslation = false;
    const locale = window.chatwootConfig?.accountLocale;
    if (locale && typeof locale === 'string') {
      const accountLocale = locale.split('_')[0]?.toLowerCase?.();
      hasAccountLocaleTranslation = !!(
        accountLocale && translations[accountLocale]?.text
      );
    }

    const hasTranslation =
      hasUserLanguageTranslation || hasAccountLocaleTranslation;
    return hasTranslation;
  }

  // Fallback to old format (single translation) for backwards compatibility
  const hasOldFormat = !!attrs.kaptionTranslation?.text;
  return hasOldFormat;
});

const renderOriginal = ref(false);

const noteCard = computed(() => parseKaptionNoteCard(content.value));

// feature-permissions.CONVERSATION.5
const shouldRenderNoteCard = computed(() => {
  return (
    isPrivate?.value === true &&
    isKaptionFlagEnabled('KAPTION_NOTE_CARDS') &&
    canUseFeature('note_cards') &&
    !!noteCard.value
  );
});

// NOTICE: KAPTION-INTEGRATION - Render the native interactive request as an
// honest dashboard event instead of exposing its transport body as plain text.
const isContactInfoRequest = computed(
  () =>
    contentAttributes.value?.kaptionRequestContactInfo === true ||
    contentAttributes.value?.kaption_request_contact_info === true
);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};

// NOTICE: KAPTION-INTEGRATION - Check if message has been edited
const isEdited = computed(() => {
  return !!contentAttributes.value?.edited;
});

// NOTICE: KAPTION-INTEGRATION - Smaller padding for bot private notes
const bubblePaddingClass = computed(() => {
  return isBotPrivateNote.value ? 'px-2 py-1' : 'px-4 py-3';
});
</script>

<template>
  <BaseBubble :class="bubblePaddingClass" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>

      <!-- NOTICE: KAPTION-INTEGRATION -->
      <!-- Use Kaption translation display if available -->
      <!-- NOTICE: KAPTION-INTEGRATION - Pass isMessageTranslating from context -->
      <div
        v-if="isContactInfoRequest"
        class="flex items-center gap-2 text-n-slate-12"
        data-testid="kaption-contact-info-request-bubble"
      >
        <KaptionPhoneRequestIcon class="size-4 shrink-0" />
        <span>{{ $t('CONVERSATION.REPLYBOX.PHONE_REQUEST.SENT_BUBBLE') }}</span>
      </div>
      <KaptionNoteCard v-else-if="shouldRenderNoteCard" :note-card="noteCard" />
      <KaptionTranslationDisplay
        v-else-if="hasKaptionTranslation || isMessageTranslating"
        :content-attributes="contentAttributes"
        :original-content="content"
        :conversation-id="conversationId"
        :message-id="id"
        :is-message-translating="isMessageTranslating"
      />

      <!-- Fallback to normal content display when no Kaption translation -->
      <template v-else>
        <FormattedContent v-if="renderContent" :content="renderContent" />
        <TranslationToggle
          v-if="hasTranslations"
          class="-mt-3"
          :showing-original="renderOriginal"
          @toggle="handleSeeOriginal"
        />
      </template>

      <AttachmentChips :attachments="attachments" class="gap-2" />
      <span v-if="isEdited" class="text-xs text-n-slate-10 italic -mt-2">
        {{ $t('CONVERSATION.EDIT_MESSAGE.EDITED') }}
      </span>
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
